import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import { BASE_URL, GET_USER_PERMISSIONS, GET_USER_AUTH_INITIAL_LOAD, POST_USER_PERMISSIONS, GET_USER_MASTER, DELETE_USER_MASTER } from '../utlilities/config';
import Swal from 'sweetalert2';
import axios from 'axios';
import AddUserModal from '../components/AddUserModal';
import Select from 'react-select';

// Icons
import { FiSearch, FiRefreshCw, FiFileText, FiPlus, FiEdit, FiSave, FiShield, FiTrash2 } from "react-icons/fi";
import { HiOutlineUsers } from "react-icons/hi";

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black-400">
      <HiOutlineUsers size={32} />
    </div>
    <h3 className="text-lg font-medium text-black-700">No users found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search</p>
  </div>
);

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const UserMaster = () => {
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'roles'
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  // Roles tab state
  const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  const token = useMemo(() => localStorage.getItem('accessToken'), []);

  const defaultHeaders = useMemo(() => {
    const headers = {
      'XApiKey': process.env.REACT_APP_API_KEY,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_USER_MASTER}`, {
        headers: defaultHeaders
      });
      if (response.status === 200 && response.data?.isSucess) {
        setUsers(response.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Could not load users', {
        autoClose: 1500,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch initial load modules (all available modules for Pharmacy)
  const fetchInitialModules = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_USER_AUTH_INITIAL_LOAD}`, {
        headers: defaultHeaders
      });
      if (response.status === 200 && response.data?.isSucess) {
        // Filter only Pharmacy modules
        const data = response.data?.data || [];
        const pharmacyModules = data.map(header => ({
          ...header,
          modules: header.modules.filter(m => m.appName === 'Pharmacy')
        })).filter(header => header.modules.length > 0);
        setAllModules(pharmacyModules);
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  }, [defaultHeaders]);

  // Fetch user permissions when user is selected
  const fetchUserPermissions = useCallback(async (userId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_USER_PERMISSIONS}`, {
        headers: {
          ...defaultHeaders,
          'userID': userId,
        },
      });
      if (response.status === 200 && response.data?.isSucess) {
        // Convert permissions array to a map for easy lookup
        const permMap = {};
        const data = response.data?.data || [];
        data.forEach(header => {
          header.modules.forEach(module => {
            permMap[module.moduleID] = module.permissions || {};
          });
        });
        setUserPermissions(permMap);
      }
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      toast.error('Could not load user permissions', { autoClose: 1500 });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  // When roles tab is active, fetch initial modules
  useEffect(() => {
    if (activeTab === 'roles') {
      fetchInitialModules();
    }
  }, [activeTab, fetchInitialModules]);

  // When user is selected for roles, fetch their permissions
  useEffect(() => {
    if (selectedUserForRoles) {
      fetchUserPermissions(selectedUserForRoles.value);
    } else {
      setUserPermissions({});
    }
  }, [selectedUserForRoles, fetchUserPermissions]);

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleAddUser = () => {
    setSelectedUserId(null);
    setSelectedUserDetails(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUserId(user.UserID);
    setSelectedUserDetails(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user) => {
    Swal.fire({
      title: 'Delete User?',
      text: `Are you sure you want to delete "${user.UserName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axios.delete(`${BASE_URL}${DELETE_USER_MASTER}`, {
            headers: {
              ...defaultHeaders,
              'UserID': user.UserID,
            },
          });
          if (response.status === 200) {
            toast.success('User deleted successfully', { autoClose: 1500 });
            fetchUsers();
          }
        } catch (error) {
          console.error('Failed to delete user:', error);
          toast.error('Failed to delete user', { autoClose: 1500 });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePermissionChange = (moduleId, permissionKey, value) => {
    setUserPermissions(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [permissionKey]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUserForRoles) {
      toast.warning('Please select a user first', { autoClose: 1500 });
      return;
    }

    setSavingPermissions(true);
    try {
      // Build the permissions payload in required format
      const modules = allModules.flatMap(header =>
        header.modules.map(module => {
          const perms = userPermissions[module.moduleID] || {};
          const permissions = [];
          
          // Add all permission types
          if (perms.Create !== undefined) {
            permissions.push({ permissionName: 'Create', isGranted: perms.Create || false });
          }
          if (perms.Edit !== undefined) {
            permissions.push({ permissionName: 'Edit', isGranted: perms.Edit || false });
          }
          if (perms.Delete !== undefined) {
            permissions.push({ permissionName: 'Delete', isGranted: perms.Delete || false });
          }
          
          // If no permissions set, add defaults
          if (permissions.length === 0) {
            permissions.push({ permissionName: 'Create', isGranted: false });
            permissions.push({ permissionName: 'Edit', isGranted: false });
            permissions.push({ permissionName: 'Delete', isGranted: false });
          }

          return {
            moduleID: module.moduleID,
            permissions: permissions
          };
        })
      );

      const payload = {
        userID: selectedUserForRoles.value,
        modules: modules
      };

      console.log(payload);

      const response = await axios.post(`${BASE_URL}${POST_USER_PERMISSIONS}`, payload, {
        headers: defaultHeaders,
      });

      if (response.status === 200) {
        toast.success('Permissions saved successfully', { autoClose: 1500 });
      }
    } catch (error) {
      console.error('Failed to save permissions:', error);
      toast.error('Failed to save permissions', { autoClose: 1500 });
    } finally {
      setSavingPermissions(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user =>
      user.UserName?.toLowerCase().includes(query) ||
      user.UserCode?.toLowerCase().includes(query) ||
      user.Designation?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const userOptions = useMemo(() => {
    return users.map(user => ({
      value: user.UserID,
      label: `${user.UserCode} - ${user.UserName}`
    }));
  }, [users]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '44px',
      borderColor: '#e5e7eb',
      '&:hover': { borderColor: '#6366f1' },
      boxShadow: 'none',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
    }),
  };

  return (
    <div className="flex h-full bg-background">
      {(loading || savingPermissions) && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      <div className="p-6 flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-black-800 flex items-center">
              <HiOutlineUsers className="mr-2" size={22} />
              User Management
            </h1>
            {activeTab === 'users' && (
              <button
                className="px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
                onClick={handleAddUser}
              >
                <FiPlus className="mr-2" /> Add New User
              </button>
            )}
            {activeTab === 'roles' && selectedUserForRoles && (
              <button
                className="px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
                onClick={handleSavePermissions}
              >
                <FiSave className="mr-2" /> Save Permissions
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex space-x-1">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center ${
                activeTab === 'users'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-black hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('users')}
            >
              <HiOutlineUsers className="mr-2" size={18} />
              Users
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center ${
                activeTab === 'roles'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-black hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('roles')}
            >
              <FiShield className="mr-2" size={18} />
              Roles
            </button>
          </div>
        </div>

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
            {/* Search */}
            <div className="bg-white rounded-xl shadow-md p-5 mb-6">
              <div className="flex flex-wrap justify-between items-center">
                <div className="relative w-full md:w-72">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black-400">
                    <FiSearch size={16} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, code or designation"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-black">
                {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                {searchQuery && ` for "${searchQuery}"`}
              </div>
              <button
                onClick={handleRefresh}
                className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
              >
                <FiRefreshCw className="mr-1" size={14} /> Refresh
              </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden">
              {filteredUsers.length > 0 ? (
                <div className="overflow-x-auto h-full">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">User Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">User Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Designation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Joining Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Max Discount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map((user, index) => (
                        <tr
                          key={user.UserID || index}
                          className={`${hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            } transition-colors hover:bg-indigo-50`}
                          onMouseEnter={() => setHoverRow(index)}
                          onMouseLeave={() => setHoverRow(null)}
                        >
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{user.UserCode || '-'}</td>
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{user.UserName || '-'}</td>
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{user.Designation || '-'}</td>
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{formatDate(user.JoiningDate)}</td>
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{user.Max_Discount_Allowed ?? 0}%</td>
                          <td className="px-6 py-2 text-sm whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.IsActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {user.IsActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                onClick={() => handleEditUser(user)}
                                title="Edit user"
                              >
                                <FiEdit size={16} />
                              </button>
                              <button
                                className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                                onClick={() => handleDeleteUser(user)}
                                title="Delete user"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoData />
              )}
            </div>
          </>
        )}

        {/* Roles Tab Content */}
        {activeTab === 'roles' && (
          <>
            {/* User Selection */}
            <div className="bg-white rounded-xl shadow-md p-5 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="w-full md:w-96">
                  <label className="block text-sm font-medium text-black mb-2">Select User</label>
                  <Select
                    options={userOptions}
                    value={selectedUserForRoles}
                    onChange={setSelectedUserForRoles}
                    placeholder="Select a user to manage permissions..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
              </div>
            </div>

            {/* Permissions Grid */}
            {selectedUserForRoles && allModules.length > 0 && (
              <div className="bg-white rounded-xl shadow-md flex-1 overflow-auto p-6">
                <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
                  <FiShield className="mr-2 text-indigo-600" />
                  Pharmacy Permissions for {selectedUserForRoles.label}
                </h2>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase">Module</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-black uppercase w-24">Create</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-black uppercase w-24">Edit</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-black uppercase w-24">Delete</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {allModules.flatMap(header => header.modules.map(m => ({ ...m, headerName: header.headerName }))).map((module, moduleIndex) => {
                        const perms = userPermissions[module.moduleID] || {};
                        const isTransaction = module.headerName === 'Transactionns';
                        return (
                          <tr key={module.moduleID} className={moduleIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 text-sm text-black">{module.moduleName}</td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={perms.Create || false}
                                onChange={(e) => handlePermissionChange(module.moduleID, 'Create', e.target.checked)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isTransaction ? (
                                <input
                                  type="checkbox"
                                  checked={perms.Edit || false}
                                  onChange={(e) => handlePermissionChange(module.moduleID, 'Edit', e.target.checked)}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                />
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isTransaction ? (
                                <input
                                  type="checkbox"
                                  checked={perms.Delete || false}
                                  onChange={(e) => handlePermissionChange(module.moduleID, 'Delete', e.target.checked)}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                />
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No user selected message */}
            {!selectedUserForRoles && (
              <div className="bg-white rounded-xl shadow-md flex-1 flex items-center justify-center">
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiShield size={32} className="text-black" />
                  </div>
                  <h3 className="text-lg font-medium text-black">Select a User</h3>
                  <p className="text-sm text-black mt-1">Choose a user from the dropdown to manage their permissions</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <AddUserModal
          setUserModal={setShowUserModal}
          getData={fetchUsers}
          iD={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          userDetails={selectedUserDetails}
          setSelectedUserDetails={setSelectedUserDetails}
        />
      )}
    </div>
  );
};

export default withAuth(UserMaster);
